import type { Aporte } from '../../lib/sesion';
import type { PropsVista } from './registro';

/**
 * Vista `nube_viva`: la nube de palabras de la sala (herramienta
 * `nube_palabras`). Cuenta términos de texto SIN autores (C7) y escala cada
 * uno por frecuencia. Respeta `opciones` del manifiesto: `titulo`,
 * `max_terminos` (recorte por frecuencia, default 40) y `anonimo` —
 * informativo: esta vista jamás dibuja autores, con o sin la bandera.
 */

/** Escalera de tamaño por frecuencia (mapeo lineal entre extremos). */
const TAMANOS = ['text-2xl', 'text-3xl', 'text-4xl', 'text-5xl', 'text-6xl'] as const;
/** Peso tipográfico que acompaña a cada peldaño (decrece con el tamaño). */
const PESOS = ['font-normal', 'font-normal', 'font-medium', 'font-semibold', 'font-semibold'] as const;

interface Termino {
  /** Forma más escrita del término, tal cual la tecleó la sala. */
  etiqueta: string;
  /** Veces que apareció (sumando variantes de mayúsculas/espacios). */
  n: number;
}

/**
 * Poda de bordes: espacios colapsados y puntuación de apertura/cierre fuera.
 * La voz llega puntuada por el transcriptor («Día completo.») y debe contar
 * igual que lo tecleado («día completo»).
 */
function podar(texto: string): string {
  return texto
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/^[¡¿"'«(\s]+/, '')
    .replace(/[.,;:!?…»")\s]+$/, '');
}

/** Clave de conteo: poda + minúsculas. */
function normalizar(texto: string): string {
  return podar(texto).toLowerCase();
}

/**
 * Tally de términos: agrupa por forma normalizada y elige como etiqueta la
 * variante escrita más frecuente (empate → la primera que llegó). Determinista.
 *
 * Entran los aportes de texto Y las notas de voz: la Pantalla ya volcó la
 * transcripción en payload.texto — la palabra dicha vale igual que la
 * tecleada. Votos, posiciones y curaduría quedan fuera.
 */
function contarTerminos(aportes: Aporte[]): Termino[] {
  // clave normalizada → { n total, variantes escritas con su conteo }
  const conteo = new Map<string, { n: number; variantes: Map<string, number> }>();
  for (const a of aportes) {
    if (a.tipo !== 'texto' && a.tipo !== 'audio') continue;
    const crudo = (a.payload ?? {}).texto;
    if (typeof crudo !== 'string') continue;
    const clave = normalizar(crudo);
    if (!clave) continue;
    const forma = podar(crudo);
    const t = conteo.get(clave) ?? { n: 0, variantes: new Map<string, number>() };
    t.n += 1;
    t.variantes.set(forma, (t.variantes.get(forma) ?? 0) + 1);
    conteo.set(clave, t);
  }

  const terminos: Termino[] = [];
  for (const { n, variantes } of conteo.values()) {
    let etiqueta = '';
    let mejor = 0;
    for (const [forma, veces] of variantes) {
      // Empate → gana la primera insertada (orden de llegada del Map).
      if (veces > mejor) {
        etiqueta = forma;
        mejor = veces;
      }
    }
    terminos.push({ etiqueta, n });
  }
  // Frecuencia descendente; empates conservan el orden de llegada (sort estable).
  terminos.sort((a, b) => b.n - a.n);
  return terminos;
}

/**
 * Intercala el ranking para que respire como nube: el más frecuente al centro
 * y el resto alternando a los lados, en vez de leerse como un ranking.
 * Determinista (sin azar).
 */
function intercalar(terminos: Termino[]): Termino[] {
  const nube: Termino[] = [];
  terminos.forEach((t, i) => {
    if (i % 2 === 0) nube.push(t);
    else nube.unshift(t);
  });
  return nube;
}

export function VistaNube({ aportes, opciones }: PropsVista) {
  const ranking = contarTerminos(aportes);
  if (ranking.length === 0) return null;

  const titulo = typeof opciones.titulo === 'string' ? opciones.titulo : 'Las palabras de la sala';
  const maxTerminos = typeof opciones.max_terminos === 'number' ? opciones.max_terminos : 40;

  const visibles = ranking.slice(0, Math.max(1, maxTerminos));
  const maxN = visibles[0].n;
  const minN = visibles[visibles.length - 1].n;

  /** Peldaño (0..4) por frecuencia: mapeo lineal entre min y max de la nube. */
  const peldano = (n: number) =>
    maxN === minN ? 0 : Math.round(((n - minN) / (maxN - minN)) * (TAMANOS.length - 1));

  return (
    <section className="mt-10 w-full max-w-4xl">
      <h2 className="mb-6 text-center font-sans text-lg uppercase tracking-wide text-content-muted">
        {titulo}
      </h2>
      <ul className="flex flex-wrap items-baseline justify-center gap-x-6 gap-y-3">
        {intercalar(visibles).map((t) => {
          const p = peldano(t.n);
          // El (o los, si empatan) más frecuente(s) llevan el acento; solo si
          // de verdad hubo repetición: con todo a 1 nadie domina la nube.
          const domina = t.n === maxN && maxN > 1;
          const clase = domina
            ? `${TAMANOS[p]} font-display font-bold text-cyan`
            : `${TAMANOS[p]} ${PESOS[p]} text-content`;
          return (
            <li key={t.etiqueta} className="flex items-baseline gap-1.5">
              <span className={`leading-tight transition-all duration-base ${clase}`}>
                {t.etiqueta}
              </span>
              {t.n > 1 && (
                <span className="text-lg font-normal tabular-nums text-content-muted">{t.n}</span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
